import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faSquareCheck, faSquareXmark} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../services/api.service';
import {validateAllFormFields} from '../../../common/utils';

export type DemoAccountRole = 'staff' | 'admin';

@Component({
    selector: 'app-add-demo-account-dialog',
    imports: [CommonModule, ReactiveFormsModule, FormsModule, FontAwesomeModule],
    templateUrl: './add-demo-account-dialog.component.html',
    styleUrl: './add-demo-account-dialog.component.css',
})
export class AddDemoAccountDialogComponent implements OnChanges {
    @Input() visible = false;
    @Output() closed = new EventEmitter<void>();
    @Output() created = new EventEmitter<void>();

    form: FormGroup;
    organizations: Array<{ id: string; name: string }> = [];
    demoExpiryDays = 7;
    demoExpiryOptions = [3, 7, 10, 15, 30];
    demoRole: DemoAccountRole = 'staff';
    saving = false;

    constructor(
        private fb: FormBuilder,
        private apiService: ApiService,
        private toastr: ToastrService,
        library: FaIconLibrary,
    ) {
        library.addIcons(faSquareCheck, faSquareXmark);
        this.form = this.fb.group({
            userName: ['', Validators.required],
            firstName: ['', Validators.required],
            lastName: [''],
            email: ['', Validators.required],
            mobileNumber: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(14)]],
            organization: ['', Validators.required],
        });
    }

    ngOnChanges(): void {
        if (this.visible) {
            this.loadOrganizations();
            this.demoRole = 'staff';
            this.demoExpiryDays = 7;
        }
    }

    close(): void {
        if (this.saving) {
            return;
        }
        this.closed.emit();
    }

    private loadOrganizations(): void {
        this.apiService.organization().subscribe({
            next: (response: any) => {
                this.organizations = Object.entries(response || {}).map(([id, name]) => ({
                    id,
                    name: String(name),
                }));
                if (this.organizations.length && !this.form.value.organization) {
                    this.form.patchValue({organization: this.organizations[0].name});
                }
            },
            error: () => this.toastr.error('Unable to load organizations.', 'Error'),
        });
    }

    submit(): void {
        if (!this.form.valid) {
            validateAllFormFields(this.form);
            return;
        }

        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        const formValues = this.form.value;
        const roleName = this.demoRole === 'admin' ? 'Admin' : 'Demo User';
        const requestData: any = {
            username: formValues.userName,
            first_name: formValues.firstName,
            last_name: formValues.lastName,
            email: formValues.email.toLowerCase(),
            mobile_number: formValues.mobileNumber,
            password,
            confirm_password: password,
            groups: [roleName],
            organization: formValues.organization,
            is_demo_user: true,
            demo_expiry_days: this.demoExpiryDays,
        };

        this.saving = true;
        this.apiService.signUp(requestData).subscribe({
            next: () => {
                this.saving = false;
                this.toastr.success(
                    this.demoRole === 'admin'
                        ? 'Demo admin account created successfully.'
                        : 'Demo staff account created successfully.',
                    'Success',
                );
                this.form.reset();
                this.created.emit();
                this.closed.emit();
            },
            error: (err) => {
                this.saving = false;
                this.handleError(err);
            },
        });
    }

    private handleError(err: any): void {
        const error = err?.error || {};
        const fields = [
            'non_field_errors',
            'mobile_number',
            'organization',
            'email',
            'username',
            'first_name',
            'last_name',
            'groups',
            'is_demo_user',
            'demo_expiry_days',
        ];
        let shown = false;
        fields.forEach((field) => {
            if (Array.isArray(error[field])) {
                error[field].forEach((message: string) => {
                    this.toastr.error(message, 'Error');
                    shown = true;
                });
            }
        });
        if (!shown && err?.status !== 403 && err?.status !== 0 && error?.detail) {
            this.toastr.error(error.detail, 'Error');
        }
    }
}
